import React from 'react';
import { Plus, Trash2, Clock, Sun, Moon, Coffee, BookOpen, Bed } from 'lucide-react';
import { BasicResourceConfigurationFormProps } from '../../../types';

interface TimeEvent {
  id: string;
  time: string;
  activity: string;
  icon: string;
  period: 'morning' | 'afternoon' | 'evening' | 'night';
}

const iconOptions = [
  { value: 'clock', label: 'Horloge', icon: Clock },
  { value: 'sun', label: 'Soleil', icon: Sun },
  { value: 'moon', label: 'Lune', icon: Moon },
  { value: 'coffee', label: 'Café', icon: Coffee },
  { value: 'book', label: 'Livre', icon: BookOpen },
  { value: 'bed', label: 'Lit', icon: Bed },
];

const periodOptions = [
  { value: 'morning', label: 'Matin' },
  { value: 'afternoon', label: 'Après-midi' },
  { value: 'evening', label: 'Soirée' },
  { value: 'night', label: 'Nuit' },
];

const themeOptions = [
  { value: 'primary', label: 'Primaire (Bleu)' },
  { value: 'secondary', label: 'Secondaire (Gris)' },
  { value: 'success', label: 'Succès (Vert)' },
  { value: 'warning', label: 'Avertissement (Jaune)' },
];

export const TimeSequenceConfigurationForm: React.FC<BasicResourceConfigurationFormProps> = ({
  resource,
  config,
  onUpdate,
}) => {
  const events = config?.events || resource.events || [
    { id: '1', time: '7:00', activity: 'Se réveiller', icon: 'sun', period: 'morning' },
    { id: '2', time: '8:00', activity: 'Prendre le petit-déjeuner', icon: 'coffee', period: 'morning' },
    { id: '3', time: '9:00', activity: 'Aller à l\'école', icon: 'book', period: 'morning' },
  ];

  const showTime = config?.showTime ?? resource.showTime ?? true;
  const showPeriod = config?.showPeriod ?? resource.showPeriod ?? true;
  const allowReordering = config?.allowReordering ?? resource.allowReordering ?? true;
  const theme = config?.theme ?? resource.theme ?? 'primary';
  const prompt = config?.prompt ?? resource.prompt ?? '';

  const addEvent = () => {
    const newEvent: TimeEvent = {
      id: Date.now().toString(),
      time: '12:00',
      activity: 'Nouvelle activité',
      icon: 'clock',
      period: 'morning',
    };
    const newEvents = [...events, newEvent];
    onUpdate({
      events: newEvents || events
    });
  };

  const removeEvent = (id: string) => {
    const newEvents = events.filter((event: any) => event.id !== id);
    onUpdate({
      events: newEvents || events
    });
  };

  const updateEvent = (id: string, field: keyof TimeEvent, value: string) => {
    const newEvents = events.map((event: any) =>
      event.id === id ? { ...event, [field]: value } : event
    );
    onUpdate({
      events: newEvents || events
    });
  };

  return (
    <div className="space-y-6">
      {/* Question */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Question ou instruction
        </label>
        <input
          type="text"
          value={prompt}
          onChange={(e) => onUpdate({ prompt: e.target.value })}
          placeholder="Organisez les activités dans l'ordre chronologique"
          className="input-field w-full"
        />
      </div>

      {/* Display Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Options d'affichage
          </label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showTime}
                onChange={(e) => onUpdate({ showTime: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm text-gray-600">Afficher l'heure</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showPeriod}
                onChange={(e) => onUpdate({ showPeriod: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm text-gray-600">Afficher la période</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={allowReordering}
                onChange={(e) => onUpdate({ allowReordering: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm text-gray-600">Permettre le réordonnancement</span>
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Thème
          </label>
          <select
            value={theme}
            onChange={(e) => onUpdate({ theme: e.target.value as any })}
            className="input-field w-full"
          >
            {themeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Events */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Activités et événements
          </label>
          <button
            type="button"
            onClick={addEvent}
            className="btn-secondary flex items-center space-x-1 text-sm"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter une activité</span>
          </button>
        </div>

        <div className="space-y-4">
          {events.map((event: any, index: number) => (
            <div key={event.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-700">
                  Activité {index + 1}
                </h4>
                <button
                  type="button"
                  onClick={() => removeEvent(event.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Heure
                  </label>
                  <input
                    type="time"
                    value={event.time}
                    onChange={(e) => updateEvent(event.id, 'time', e.target.value)}
                    className="input-field w-full"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Activité
                  </label>
                  <input
                    type="text"
                    value={event.activity}
                    onChange={(e) => updateEvent(event.id, 'activity', e.target.value)}
                    placeholder="Description de l'activité"
                    className="input-field w-full"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Icône
                  </label>
                  <select
                    value={event.icon}
                    onChange={(e) => updateEvent(event.id, 'icon', e.target.value)}
                    className="input-field w-full"
                  >
                    {iconOptions.map(option => {
                      return (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Période
                  </label>
                  <select
                    value={event.period}
                    onChange={(e) => updateEvent(event.id, 'period', e.target.value)}
                    className="input-field w-full"
                  >
                    {periodOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preview */}
      {events.length > 0 && (
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Aperçu</h4>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="space-y-2">
              {events.slice(0, 3).map((event: any, index: number) => (
                <div key={event.id} className="flex items-center space-x-3 text-sm">
                  <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs">
                    {index + 1}
                  </div>
                  <span className="text-gray-600">{event.time}</span>
                  <span className="font-medium">{event.activity}</span>
                  <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                    {event.period === 'morning' && 'Matin'}
                    {event.period === 'afternoon' && 'Après-midi'}
                    {event.period === 'evening' && 'Soirée'}
                    {event.period === 'night' && 'Nuit'}
                  </span>
                </div>
              ))}
              {events.length > 3 && (
                <div className="text-xs text-gray-500">
                  ... et {events.length - 3} autres activités
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
